import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import "../../styles/MergedCharges.css";
import expanded_icon from "../../assets/svg/expanded_icon.svg";
import delete_icon from "../../assets/svg/combined_charges_delete.svg";
import edit_icon from "../../assets/svg/combined_charges_edit.svg";

const makeUniqueId = (line) =>
    `${line.id}_${line.sap_id ?? line.sap_code}_${line.job_type_id}`;

const groupLinesBySap = (lines) => {
    const map = {};
    lines.forEach((line) => {
        const key = line.sap_id ?? line.sap_code;
        if (!map[key]) {
            map[key] = {
                sap_id: line.sap_id ?? null,
                sap_code: line.sap_code,
                sap_key: key,
                sap_description: line.sap_description,
                rows: [],
            };
        }
        map[key].rows.push({
            ...line,
            uniqueId: makeUniqueId(line),
            isChecked: false,
            isCombined: false,
        });
    });
    return Object.values(map);
};

const BillingAutomationMergedCharges = ({
    isOpen,
    onClose,
    onComplete,
    selectedLines = [],
    invoiceType,
    existingLines
}) => {
    const [groupedData, setGroupedData] = useState([]);
    const [combinedCharges, setCombinedCharges] = useState([]);
    const [expandedIds, setExpandedIds] = useState([]);

    useEffect(() => {
        if (!isOpen || selectedLines.length === 0) return;

        const grouped = groupLinesBySap(selectedLines);

        if (existingLines?.length) {
            const restoredCombined = [];
            const combinedOriginalIds = new Set();

            existingLines.forEach(line => {
                if (line.isMergedCombined && line.originalRows) {
                    restoredCombined.push({
                        combinedId: line.id,
                        sap_id: line.originalRows[0]?.sap_id,
                        sap_code: line.sap_code,
                        sap_key: line.originalRows[0]?.sap_id ?? line.sap_code,
                        sap_description: line.sap_description,
                        charge_code: line.charge_code,
                        charge_name: line.charge_name,
                        combined_quantity: line.unit_of_quantity,
                        combined_rate: line.unit_of_rate,
                        combined_net_total: line.net_total,
                        originalRows: line.originalRows,
                    });
                    line.originalRows.forEach(r => combinedOriginalIds.add(r.uniqueId));
                }
            });

            const restoredGrouped = grouped.map(group => ({
                ...group,
                rows: group.rows.map(row => ({
                    ...row,
                    isCombined: combinedOriginalIds.has(row.uniqueId),
                })),
            }));

            setGroupedData(restoredGrouped);
            setCombinedCharges(restoredCombined);
        } else {
            setGroupedData(grouped);
            setCombinedCharges([]);
        }

        setExpandedIds([]);
    }, [isOpen, selectedLines]);

    useEffect(() => {
        if (!isOpen) {
            setGroupedData([]);
            setCombinedCharges([]);
            setExpandedIds([]);
        }
    }, [isOpen]);

    const toggleRowCheck = (sapKey, uniqueId) => {
        setGroupedData((prev) =>
            prev.map((group) => {
                if (group.sap_key !== sapKey) return group;
                return {
                    ...group,
                    rows: group.rows.map((row) =>
                        row.uniqueId === uniqueId ? { ...row, isChecked: !row.isChecked } : row
                    ),
                };
            })
        );
    };

    const toggleGroupCheck = (sapKey) => {
        setGroupedData((prev) =>
            prev.map((group) => {
                if (group.sap_key !== sapKey) return group;
                const uncombined = group.rows.filter((r) => !r.isCombined);
                const allChecked = uncombined.length > 0 && uncombined.every((r) => r.isChecked);
                return {
                    ...group,
                    rows: group.rows.map((row) =>
                        row.isCombined ? row : { ...row, isChecked: !allChecked }
                    ),
                };
            })
        );
    };

    const handleCombine = () => {
        const selectedBySapKey = {};
        groupedData.forEach((group) => {
            group.rows.forEach((row) => {
                if (row.isChecked && !row.isCombined) {
                    if (!selectedBySapKey[group.sap_key]) {
                        selectedBySapKey[group.sap_key] = {
                            sap_id: group.sap_id,
                            sap_key: group.sap_key,
                            sap_code: group.sap_code,
                            sap_description: group.sap_description,
                            originalRows: [],
                        };
                    }
                    selectedBySapKey[group.sap_key].originalRows.push(row);
                }
            });
        });

        const groups = Object.values(selectedBySapKey);
        if (groups.length === 0) return;

        const newCombined = groups.map((g) => {
            const totalQty = g.originalRows.reduce((s, r) => s + parseFloat(r.unit_of_quantity || 0), 0);
            const totalNet = g.originalRows.reduce((s, r) => s + parseFloat(r.net_total || 0), 0);
            const rate = totalQty > 0 ? totalNet / totalQty : 0;

            return {
                combinedId: `combined_${g.sap_key}_${Date.now()}`,
                sap_id: g.sap_id,
                sap_key: g.sap_key,
                sap_code: g.sap_code,
                sap_description: g.sap_description,
                charge_code: g.originalRows[0]?.charge_code || '',
                charge_name: g.originalRows[0]?.charge_name || g.sap_description,
                combined_quantity: 1,
                combined_rate: parseFloat(rate.toFixed(2)),
                combined_net_total: parseFloat(totalNet.toFixed(2)),
                originalRows: g.originalRows,
            };
        });

        setCombinedCharges((prev) => [...prev, ...newCombined]);

        const combinedLineIds = new Set(
            groups.flatMap((g) => g.originalRows.map((r) => r.uniqueId))
        );

        setGroupedData((prev) =>
            prev.map((group) => ({
                ...group,
                rows: group.rows.map((row) =>
                    combinedLineIds.has(row.uniqueId)
                        ? { ...row, isCombined: true, isChecked: false }
                        : row
                ),
            }))
        );
    };

    const handleRevert = (combinedId) => {
        const item = combinedCharges.find((c) => c.combinedId === combinedId);
        if (!item) return;

        const revertedIds = new Set(item.originalRows.map((r) => r.uniqueId));

        setGroupedData((prev) =>
            prev.map((group) => {
                if (group.sap_key !== item.sap_key) return group;
                return {
                    ...group,
                    rows: group.rows.map((row) =>
                        revertedIds.has(row.uniqueId)
                            ? { ...row, isCombined: false, isChecked: false }
                            : row
                    ),
                };
            })
        );

        setCombinedCharges((prev) => prev.filter((c) => c.combinedId !== combinedId));
        setExpandedIds((prev) => prev.filter((id) => id !== combinedId));
    };

    const handleRevertSingle = (combinedId, uniqueId) => {
        const item = combinedCharges.find((c) => c.combinedId === combinedId);
        if (!item) return;

        const remainingRows = item.originalRows.filter((r) => r.uniqueId !== uniqueId);

        setGroupedData((prev) =>
            prev.map((group) => {
                if (group.sap_key !== item.sap_key) return group;   
                return {
                    ...group,
                    rows: group.rows.map((row) =>
                        row.uniqueId === uniqueId
                            ? { ...row, isCombined: false, isChecked: false }
                            : row
                    ),
                };
            })
        );

        if (remainingRows.length === 0) {
            setCombinedCharges((prev) => prev.filter((c) => c.combinedId !== combinedId));
            setExpandedIds((prev) => prev.filter((id) => id !== combinedId));
        } else {
            const totalNet = remainingRows.reduce((s, r) => s + parseFloat(r.net_total || 0), 0);
            const totalQty = remainingRows.reduce((s, r) => s + parseFloat(r.unit_of_quantity || 0), 0);
            setCombinedCharges((prev) =>
                prev.map((c) =>
                    c.combinedId === combinedId
                        ? {
                            ...c,
                            originalRows: remainingRows,
                            combined_net_total: parseFloat(totalNet.toFixed(2)),
                            combined_rate: parseFloat((totalQty > 0 ? totalNet / totalQty : 0).toFixed(2)),
                        }
                        : c
                )
            );
        }
    };

    const toggleExpand = (combinedId) => {
        setExpandedIds((prev) =>
            prev.includes(combinedId)
                ? prev.filter((id) => id !== combinedId)
                : [...prev, combinedId]
        );
    };

    const hasUncombined = useMemo(
        () => groupedData.some((g) => g.rows.some((r) => !r.isCombined)),
        [groupedData]
    );

    const hasSelected = useMemo(
        () => groupedData.some((g) => g.rows.some((r) => r.isChecked && !r.isCombined)),
        [groupedData]
    );

    const buildFinalLines = () => {
        const lines = [];

        combinedCharges.forEach((item) => {
            const firstRow = item.originalRows[0];
            lines.push({
                id: item.combinedId,
                charge_code: item.charge_code,
                charge_name: item.sap_description,
                remarks: firstRow?.remarks || '--',
                po_wo_number: firstRow?.po_wo_number || null,  
                po_wo_date: firstRow?.po_wo_date || null,
                sap_id: item.sap_id ?? null,
                sap_code: item.sap_code,
                sap_description: item.sap_description,
                unit_of_quantity: item.combined_quantity,
                unit_of_rate: item.combined_rate,
                net_total: item.combined_net_total,
                isMergedCombined: true,
                originalRows: item.originalRows,
            });
        });

        groupedData.forEach((group) => {
            group.rows
                .filter((r) => !r.isCombined)
                .forEach((row) => {
                    lines.push({ ...row, sap_id: row.sap_id ?? group.sap_id ?? null, isMergedCombined: false });
                });
        });

        return lines;
    };

    const handleSubmit = () => {
        const finalLines = buildFinalLines();
        onComplete(finalLines);
        onClose();
    };

    const handleSeparateInvoice = () => {
        const finalLines = buildFinalLines();
        onComplete(finalLines);
        onClose();
    };

    const isMergedSingle = invoiceType === 'merged_single_invoice';
    const isMergedSeparate = invoiceType === 'merged_separate_invoice';

    if (!isOpen) return null;

    return (
        <div className="merged-list-overlay">
            <div
                className="merged-list-container"
                style={{ overflowX: 'auto', whiteSpace: 'nowrap', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}
            >

                {combinedCharges.length > 0 && (
                    <div className="combine-charges-wrapper">
                        <div className="combine-charges-header sticky-header">
                            <div className="combine-charges-left">
                                <h2>Combined Charges</h2>
                                <div className="combine-charges-info">
                                    <span className="combine-charges-info-icon">!</span>
                                    <span>Combined rows below. Expand to see original lines or revert.</span>
                                </div>
                            </div>
                            {/* <div className="combine-charges-right-buttons">
                                {isMergedSeparate && (
                                    <button className="combine-charges-btn" onClick={handleSeparateInvoice}>
                                        Separate Invoice
                                    </button>
                                )}
                            </div> */}
                        </div>

                        <div
                            className="merged-list-content"
                            style={{ overflowX: 'auto', whiteSpace: 'nowrap', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}
                        >
                            <div className="invoice_popup_outercategory" style={{ backgroundColor: '#FFF7E8' }}>
                                <div className="invoice_popup_category_subheader">
                                    <div className="invoice_popup_element_list" style={{ overflowX: 'auto' }}>
                                        <div className="table-container" style={{ position: 'relative' }}>
                                            <table className="invoice_table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ backgroundColor: '#E6E9EE', width: 40, textAlign: 'center' }}></th>
                                                        <th style={{ backgroundColor: '#E6E9EE', width: 40, textAlign: 'center' }}></th>
                                                        <th style={{ backgroundColor: '#E6E9EE', textAlign: 'center' }}>S.NO</th>
                                                        <th style={{ backgroundColor: '#E6E9EE', textAlign: 'center' }}>CHARGE CODE</th>
                                                        <th style={{ backgroundColor: '#E6E9EE', textAlign: 'center' }}>SAP CODE</th>
                                                        <th style={{ backgroundColor: '#E6E9EE', textAlign: 'center' }}>SAP DESCRIPTION</th>
                                                        <th style={{ backgroundColor: '#E6E9EE', textAlign: 'center' }}>QTY</th>
                                                        <th style={{ backgroundColor: '#E6E9EE', textAlign: 'center' }}>RATE</th>
                                                        <th style={{ backgroundColor: '#E6E9EE', textAlign: 'center' }}>NET TOTAL</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {combinedCharges.map((item, index) => {
                                                        const isExpanded = expandedIds.includes(item.combinedId);
                                                        return (
                                                            <React.Fragment key={item.combinedId}>
                                                                <tr style={{ backgroundColor: '#FFFBEB' }}>
                                                                    {/* Revert whole */}
                                                                    <td
                                                                        style={{ textAlign: 'center', cursor: 'pointer' }}
                                                                        onClick={() => handleRevert(item.combinedId)}
                                                                        title="Revert entire combined row"
                                                                    >
                                                                        <img src={delete_icon} alt="Delete" />
                                                                    </td>
                                                                    {/* Expand toggle */}
                                                                    <td
                                                                        style={{ textAlign: 'center', cursor: 'pointer' }}
                                                                        onClick={() => toggleExpand(item.combinedId)}
                                                                    >
                                                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                                                            <img
                                                                                src={isExpanded ? expanded_icon : edit_icon}
                                                                                alt={isExpanded ? "Expanded" : "Edit"}
                                                                                style={{
                                                                                    width: "16px",
                                                                                    height: "16px",
                                                                                    display: "block"
                                                                                }}
                                                                            />
                                                                            <span
                                                                                style={{
                                                                                    minWidth: 22, height: 22, borderRadius: 11,
                                                                                    backgroundColor: '#E0EDFF', color: '#1D4ED8',
                                                                                    fontSize: 11, fontWeight: 600,
                                                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                                    padding: '0 4px',
                                                                                }}
                                                                            >
                                                                                {item.originalRows.length}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{index + 1}</td>
                                                                    <td>{item.charge_code}</td>
                                                                    <td style={{ fontWeight: 600, color: '#92400E' }}>{item.sap_code}</td>
                                                                    <td>{item.sap_description}</td>
                                                                    <td>{item.combined_quantity}</td>
                                                                    <td>{item.combined_rate}</td>
                                                                    <td style={{ fontWeight: 600 }}>₹ {Number(item.combined_net_total).toLocaleString()}</td>
                                                                </tr>

                                                                {/* Expanded original rows */}
                                                                {isExpanded && item.originalRows.map((row, ri) => (
                                                                    <tr key={ri}>
                                                                        <td colSpan={9} style={{ padding: '8px 16px 8px 56px' }}>
                                                                            <div
                                                                                style={{
                                                                                    background: '#E6FFE6',
                                                                                    borderRadius: 14,
                                                                                    padding: '10px 20px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 30,
                                                                                    borderLeft: '4px solid #9BE3A4',
                                                                                }}
                                                                            >
                                                                                {/* Revert single */}
                                                                                <div
                                                                                    style={{ cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                                                                                    onClick={() => handleRevertSingle(item.combinedId, row.uniqueId)}
                                                                                    title="Revert this row"
                                                                                >
                                                                                    <img src={delete_icon} alt="Delete" />
                                                                                </div>

                                                                                <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
                                                                                    {[
                                                                                        ['JOB TYPE', row.job_type_name],
                                                                                        ['CHARGE CODE', row.charge_code],
                                                                                        ['DESCRIPTION', row.charge_name],
                                                                                        ['PO NUMBER', row.po_wo_number || '--'],
                                                                                        ['PO DATE', row.po_wo_date || '--'],
                                                                                        ['REMARKS', row.remarks || '--'],
                                                                                        ['QTY', row.unit_of_quantity],
                                                                                        ['RATE', row.unit_of_rate],
                                                                                        ['NET TOTAL', `₹ ${Number(row.net_total).toLocaleString()}`],
                                                                                    ].map(([label, value]) => (
                                                                                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                                            <div style={{ fontSize: 10, color: '#7A7A7A', marginBottom: 8 }}>{label}</div>
                                                                                            <div style={{ fontWeight: 600 }}>{value}</div>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── UNCOMBINED / GROUPED CHARGES SECTION ─────────────────────── */}
                <div className="merged-charges-wrapper">
                    <div className="merged-list-header sticky-header">
                        <div className="merged-left">
                            <h2>
                                {combinedCharges.length > 0 ? 'Uncombined Charges' : 'Charges Grouped by SAP Code'}
                            </h2>
                            <div className="merged-info">
                                <span className="info-icon">!</span>
                                <span>Select rows within the same SAP group and click Combine.</span>
                            </div>
                        </div>
                        <div className="merged-right-buttons">
                            <button
                                className="combine-btn"
                                onClick={handleCombine}
                                disabled={!hasSelected}
                                style={{ cursor: hasSelected ? 'pointer' : 'not-allowed', opacity: hasSelected ? 1 : 0.5 }}
                            >
                                Combine
                            </button>
                            <div className="merged-close-btn" onClick={onClose}>
                                <X size={16} />
                            </div>
                        </div>
                    </div>

                    <div
                        className="merged-list-content"
                        style={{ overflowX: 'auto', whiteSpace: 'nowrap', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}
                    >
                        {hasUncombined ? (
                            groupedData
                                .filter((g) => g.rows.some((r) => !r.isCombined))
                                .map((group) => {
                                    const uncombinedRows = group.rows.filter((r) => !r.isCombined);
                                    const allChecked = uncombinedRows.length > 0 && uncombinedRows.every((r) => r.isChecked);
                                    const someChecked = uncombinedRows.some((r) => r.isChecked);

                                    const selectedQty = uncombinedRows.filter((r) => r.isChecked).reduce((s, r) => s + parseFloat(r.unit_of_quantity || 0), 0);
                                    const selectedNet = uncombinedRows.filter((r) => r.isChecked).reduce((s, r) => s + parseFloat(r.net_total || 0), 0);

                                    return (
                                        <div key={group.sap_key} className="invoice_popup_outercategory">
                                            {/* Group header */}
                                            <div className="invoice_popup_category_header">
                                                <div
                                                    className="category_name_left"
                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                                                >
                                                    <div style={{ display: 'flex', gap: 10, fontSize: 11, alignItems: 'center', overflowX: 'auto', justifyContent: 'flex-start' }}>
                                                        {group.sap_code && (
                                                            <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, height: 30, whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                                                <span style={{ color: '#C89C00', fontWeight: 600 }}>SAP CODE :</span>
                                                                <span>{group.sap_code}</span>
                                                            </div>
                                                        )}
                                                        {group.sap_description && (
                                                            <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, height: 30, whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                                                <span style={{ color: 'green', fontWeight: 600 }}>SAP DESC :</span>
                                                                <span>{group.sap_description}</span>
                                                            </div>
                                                        )}
                                                        {someChecked && (
                                                            <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, height: 30, whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #FC8181' }}>
                                                                <span style={{ color: '#E53E3E', fontWeight: 600 }}>SELECTED :</span>
                                                                <span>Qty: {selectedQty.toFixed(2)} | Net: ₹ {selectedNet.toFixed(2)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Group table */}
                                            <div className="invoice_popup_category_subheader">
                                                <div className="invoice_popup_element_list" style={{ overflowX: 'auto' }}>
                                                    <div className="table-container" style={{ position: 'relative' }}>
                                                        <table className="invoice_table">
                                                            <thead>
                                                                <tr>
                                                                    <th
                                                                        rowSpan={2}
                                                                        style={{ width: 44, textAlign: 'center', backgroundColor: '#D9FFE2' }}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={allChecked}
                                                                            ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                                                            onChange={() => toggleGroupCheck(group.sap_key)}
                                                                            style={{ width: 15, height: 15, margin: '0 auto' }}
                                                                        />
                                                                    </th>
                                                                    <th
                                                                        colSpan={10}
                                                                        style={{ backgroundColor: '#D9FFE2', color: '#008B1F', textAlign: 'center' }}
                                                                    >
                                                                        SALES
                                                                    </th>
                                                                </tr>
                                                                <tr>
                                                                    {['S.NO', 'JOB TYPE', 'CHARGE CODE', 'DESCRIPTION', 'PO NUMBER', 'PO DATE', 'REMARKS', 'QTY', 'RATE', 'NET TOTAL'].map((h) => (
                                                                        <th key={h} style={{ backgroundColor: '#D9FFE2', textAlign: 'center' }}>{h}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {uncombinedRows.map((row, ri) => (
                                                                    <tr
                                                                        key={row.uniqueId}
                                                                        style={{ backgroundColor: row.isChecked ? '#F0FFF4' : '#fff', transition: 'background-color 0.15s ease' }}
                                                                    >
                                                                        <td style={{ textAlign: 'center', width: 44, padding: '8px 0' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={row.isChecked}
                                                                                onChange={() => toggleRowCheck(group.sap_key, row.uniqueId)}
                                                                                style={{ width: 15, height: 15, margin: '0 auto' }}
                                                                            />
                                                                        </td>
                                                                        <td>{ri + 1}</td>
                                                                        <td>{row.job_type_name}</td>
                                                                        <td style={{ fontWeight: 600, padding: 12 }}>{row.charge_code}</td>
                                                                        <td>{row.charge_name}</td>
                                                                        <td>{row.po_wo_number || '--'}</td>
                                                                        <td>{row.po_wo_date || '--'}</td>
                                                                        <td>{row.remarks || '--'}</td>
                                                                        <td>{row.unit_of_quantity}</td>
                                                                        <td>{row.unit_of_rate}</td>
                                                                        <td style={{ fontWeight: 600 }}>₹ {Number(row.net_total).toLocaleString()}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                        ) : (
                            <div style={{ textAlign: 'center', padding: '48px 24px', color: '#276749' }}>
                                <div style={{ fontSize: 40, marginBottom: 12, color: '#68D391' }}>✓</div>
                                <h3 style={{ margin: '0 0 6px', color: '#276749' }}>All charges combined</h3>
                                <p style={{ margin: 0, color: '#68D391', fontSize: 13 }}>
                                    No uncombined charges remaining. Proceed with Submit.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── FOOTER ACTIONS ────────────────────────────────────────────── */}
                <div className="combined-footer-container">
                    <div className="combined-footer-actions">
                        <button className="combined-btn cancel" onClick={onClose}>
                            Cancel
                        </button>
                        {isMergedSingle && (
                            <button className="combined-btn submit" onClick={handleSubmit}>
                                Submit
                            </button>
                        )}
                        {isMergedSeparate && (
                            <button className="combined-btn submit" onClick={handleSeparateInvoice}>
                                Separate Invoice
                            </button>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default BillingAutomationMergedCharges;